import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, outlineButton, labelStyle, inputStyle, pillButton } from './adminTheme';
import { buildNotificationHtml, LOGO_OPTIONS, SENDER_NAME_OPTIONS, CITY_PRESETS, TIMEZONE_OPTIONS } from './emailTemplates';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const SITE_URL = 'https://winston.longden.site';

// Défini localement (plutôt qu'importé d'adminTheme.js) pour que ce fichier
// reste indépendant de la version d'adminTheme.js actuellement déployée —
// certains déploiements n'ont pas encore l'export `chipButton`.
const chipButton = {
    padding: '6px 12px',
    background: 'none',
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: 0,
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: font,
};

const T = {
    fr: {
        cancel: '← Annuler',
        senderName: "Nom de l'expéditeur",
        logo: 'Logo',
        citiesFooter: 'Villes affichées dans le footer',
        timezone: 'Fuseau horaire',
        extraMessage: 'Message additionnel (optionnel)',
        extraMessagePh: 'Un mot en plus à ajouter...',
        confirmAndSend: 'Confirmer et envoyer',
        previewSentBy: (name) => `Aperçu — envoyé par "${name}"`,
        intro: "Notifie les abonnés d'un nouveau post ou album publié.",
        recentPosts: 'Posts récents',
        recentAlbums: 'Albums récents',
        noPosts: 'Aucun post.',
        noAlbums: 'Aucun album.',
        notified: 'Notifié',
        notNotified: 'Pas encore',
        renotify: 'Renotifier',
        notify: 'Notifier',
        loading: 'Chargement...',
        confirmNotify: (title) => `Notifier les abonnés : "${title}" ?`,
        failed: (msg) => `Échec : ${msg}`,
        partial: (sent, failed) => `${sent} envoyé(s), ${failed} échec(s).`,
        success: (sent) => `Abonnés notifiés — ${sent} envoyés.`,
    },
    en: {
        cancel: '← Cancel',
        senderName: 'Sender name',
        logo: 'Logo',
        citiesFooter: 'Cities shown in the footer',
        timezone: 'Timezone',
        extraMessage: 'Extra message (optional)',
        extraMessagePh: 'Anything else to add...',
        confirmAndSend: 'Confirm and send',
        previewSentBy: (name) => `Preview — sent by "${name}"`,
        intro: 'Notifies subscribers about a newly published post or album.',
        recentPosts: 'Recent posts',
        recentAlbums: 'Recent albums',
        noPosts: 'No posts.',
        noAlbums: 'No albums.',
        notified: 'Notified',
        notNotified: 'Not yet',
        renotify: 'Renotify',
        notify: 'Notify',
        loading: 'Loading...',
        confirmNotify: (title) => `Notify subscribers about "${title}"?`,
        failed: (msg) => `Failed: ${msg}`,
        partial: (sent, failed) => `${sent} sent, ${failed} failed.`,
        success: (sent) => `Subscribers notified — ${sent} sent.`,
    },
};

function AdminNotify() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [posts, setPosts] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingId, setSendingId] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');
    const [previewItem, setPreviewItem] = useState(null);
    const [customMessage, setCustomMessage] = useState('');
    const [senderName, setSenderName] = useState('winston.ie by Longden Collective');
    const [logoUrl, setLogoUrl] = useState(LOGO_OPTIONS[0].url);
    const [selectedCities, setSelectedCities] = useState(['Montréal, Canada']);
    const [timezone, setTimezone] = useState(TIMEZONE_OPTIONS[0].tz);

    const fetchData = async () => {
        setLoading(true);
        const { data: postsData } = await supabase.from('posts').select('*').order('created_at', { ascending: false }).limit(15);
        const { data: albumsData } = await supabase.from('albums').select('*').eq('archived', false).order('date', { ascending: false }).limit(15);
        if (postsData) setPosts(postsData);
        if (albumsData) setAlbums(albumsData);
        setLoading(false);
    };

    useEffect(() => { fetchData(); }, []);

    const toggleCity = (city) => {
        setSelectedCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);
    };

    const buildPayload = (item, type) => {
        const link = type === 'post' ? `${SITE_URL}/blog/${item.slug}` : `${SITE_URL}/region/${item.region}/${item.folder}`;
        const heroImageUrl = item.cover_url || item.image_url || '';
        const subtitle = type === 'post' ? (item.excerpt || item.content?.slice(0, 140) || '') : [item.location, item.date].filter(Boolean).join(' · ');
        const authorName = type === 'album' ? (item.created_by || '') : '';

        return {
            isNotification: true,
            type,
            authorName,
            itemTitle: item.title,
            subtitle,
            customMessage: customMessage.trim() || undefined,
            heroImageUrl,
            ctaUrl: link,
            logoUrl,
            senderName,
            showDateline: selectedCities.length > 0,
            datelineCities: selectedCities,
            timezone,
            datelineAlign: 'center',
        };
    };

    const openPreview = (item, type) => {
        setCustomMessage('');
        const authorName = type === 'album' ? (item.created_by || '') : '';
        setSenderName(authorName ? `${authorName} for winston.ie` : 'winston.ie by Longden Collective');
        setPreviewItem({ item, type });
    };

    const handleConfirmSend = async () => {
        if (!previewItem) return;
        const { item, type } = previewItem;
        if (!window.confirm(t.confirmNotify(item.title))) return;

        setSendingId(item.id || item.folder);
        setStatusMsg('');
        setPreviewItem(null);

        try {
            const { data, error } = await supabase.functions.invoke('send-newsletter', { body: buildPayload(item, type) });
            if (error) throw error;

            const table = type === 'post' ? 'posts' : 'albums';
            const idField = type === 'post' ? 'id' : 'folder';
            await supabase.from(table).update({ notified: true }).eq(idField, item[idField]);

            setStatusMsg(data?.failed > 0 ? t.partial(data.sent, data.failed) : t.success(data?.sent || 0));
            fetchData();
        } catch (err) {
            setStatusMsg(t.failed(err.message || err));
        } finally {
            setSendingId(null);
        }
    };

    if (loading) return <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>;

    if (previewItem) {
        const payload = buildPayload(previewItem.item, previewItem.type);
        const previewHtml = buildNotificationHtml(payload);
        const authorName = previewItem.type === 'album' ? (previewItem.item.created_by || '') : '';

        return (
            <div>
                <button className="wlink" onClick={() => setPreviewItem(null)} style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: '11px', fontWeight: 500, cursor: 'pointer', fontFamily: font, marginBottom: '16px' }}>
                    {t.cancel}
                </button>

                <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 320px', minWidth: '280px' }}>
                        <p style={labelStyle}>{t.senderName}</p>
                        <select value={senderName} onChange={e => setSenderName(e.target.value)} style={{ ...inputStyle, marginBottom: '20px' }}>
                            {SENDER_NAME_OPTIONS(authorName).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>

                        <p style={labelStyle}>{t.logo}</p>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            {LOGO_OPTIONS.map(opt => (
                                <div key={opt.url} onClick={() => setLogoUrl(opt.url)} style={{ flex: 1, padding: '10px', border: `1px solid ${logoUrl === opt.url ? theme.text : theme.border}`, borderRadius: 0, cursor: 'pointer', textAlign: 'center' }}>
                                    <img src={opt.url} alt={opt.label} style={{ height: '24px', margin: '0 auto 6px', display: 'block' }} />
                                    <p style={{ fontFamily: font, fontSize: '9.5px', color: theme.textMuted, margin: 0 }}>{opt.label}</p>
                                </div>
                            ))}
                        </div>

                        <p style={labelStyle}>{t.citiesFooter}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                            {CITY_PRESETS.map(city => (
                                <button
                                    key={city}
                                    onClick={() => toggleCity(city)}
                                    style={{
                                        ...chipButton,
                                        background: selectedCities.includes(city) ? theme.text : 'none',
                                        color: selectedCities.includes(city) ? '#fff' : theme.text,
                                        fontSize: '10.5px',
                                    }}
                                >
                                    {city}
                                </button>
                            ))}
                        </div>

                        <p style={labelStyle}>{t.timezone}</p>
                        <select value={timezone} onChange={e => setTimezone(e.target.value)} style={{ ...inputStyle, marginBottom: '20px' }}>
                            {TIMEZONE_OPTIONS.map(opt => <option key={opt.tz} value={opt.tz}>{opt.label}</option>)}
                        </select>

                        <p style={labelStyle}>{t.extraMessage}</p>
                        <textarea rows={4} value={customMessage} onChange={e => setCustomMessage(e.target.value)} style={{ ...inputStyle, resize: 'vertical', marginBottom: '20px' }} placeholder={t.extraMessagePh} />

                        <button className="wlink" onClick={handleConfirmSend} style={{ ...pillButton, width: '100%' }}>
                            {t.confirmAndSend}
                        </button>
                    </div>

                    <div style={{ flex: '1 1 380px', minWidth: '320px' }}>
                        <p style={labelStyle}>{t.previewSentBy(senderName)}</p>
                        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 0, overflow: 'hidden', background: '#fff' }}>
                            <iframe title="preview" srcDoc={previewHtml} style={{ width: '100%', height: '680px', border: 'none' }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <p style={{ fontFamily: font, fontSize: '11.5px', color: theme.textMuted, margin: '0 0 24px' }}>
                {t.intro}
            </p>

            {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: '0 0 20px' }}>{statusMsg}</p>}

            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: '1 1 320px', minWidth: '300px' }}>
                    <p style={{ ...labelStyle, marginBottom: '12px' }}>{t.recentPosts}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '480px', overflowY: 'auto', paddingRight: '4px' }}>
                        {posts.length === 0 ? <p style={{ fontFamily: font, fontSize: '12px', color: theme.textMuted }}>{t.noPosts}</p> : posts.map(p => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 12px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '12.5px', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</p>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '9.5px', color: theme.textMuted }}>{p.notified ? t.notified : t.notNotified} · {new Date(p.created_at).toLocaleDateString('fr-CA')}</p>
                                </div>
                                <button className="wlink" onClick={() => openPreview(p, 'post')} disabled={sendingId === p.id} style={{ ...outlineButton, opacity: sendingId === p.id ? 0.5 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {sendingId === p.id ? '...' : p.notified ? t.renotify : t.notify}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ flex: '1 1 320px', minWidth: '300px' }}>
                    <p style={{ ...labelStyle, marginBottom: '12px' }}>{t.recentAlbums}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '480px', overflowY: 'auto', paddingRight: '4px' }}>
                        {albums.length === 0 ? <p style={{ fontFamily: font, fontSize: '12px', color: theme.textMuted }}>{t.noAlbums}</p> : albums.map(a => (
                            <div key={a.folder} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 12px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '12.5px', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</p>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '9.5px', color: theme.textMuted }}>{a.notified ? t.notified : t.notNotified} · {a.location || a.region}</p>
                                </div>
                                <button className="wlink" onClick={() => openPreview(a, 'album')} disabled={sendingId === a.folder} style={{ ...outlineButton, opacity: sendingId === a.folder ? 0.5 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {sendingId === a.folder ? '...' : a.notified ? t.renotify : t.notify}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminNotify;